import { useState, useEffect, useCallback } from "react";
import { statisticsApi, ApiError } from "../lib/api";
import type { TimeRange, StatisticsData } from "@runlens/shared";

interface UseStatisticsResult {
  statistics: StatisticsData | null;
  loading: boolean;
  error: string | null;
  timeRange: TimeRange;
  setTimeRange: (range: TimeRange) => void;
  refresh: () => void;
}

/**
 * Hook for fetching and managing statistics data.
 * Automatically fetches on mount and when time range changes.
 */
export function useStatistics(initialRange: TimeRange = "1y"): UseStatisticsResult {
  const [statistics, setStatistics] = useState<StatisticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<TimeRange>(initialRange);

  const fetchStatistics = useCallback(async (range: TimeRange) => {
    setLoading(true);
    setError(null);

    try {
      const data = await statisticsApi.get(range);
      setStatistics(data);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to fetch statistics");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on mount and when time range changes
  useEffect(() => {
    fetchStatistics(timeRange);
  }, [timeRange, fetchStatistics]);

  const handleSetTimeRange = useCallback((range: TimeRange) => {
    setTimeRange(range);
  }, []);

  const refresh = useCallback(() => {
    fetchStatistics(timeRange);
  }, [timeRange, fetchStatistics]);

  return {
    statistics,
    loading,
    error,
    timeRange,
    setTimeRange: handleSetTimeRange,
    refresh,
  };
}
